import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import { ParallaxImage } from "./ParallaxImage";

const CAT = { windows: ["Finestre", "Windows"], doors: ["Porte", "Doors"], facades: ["Facciate", "Facades"], outdoor: ["Vetrate & Pergole", "Glass Rooms & Pergolas"], awnings: ["Tende da Sole", "Awnings"], railings: ["Balaustre in Vetro", "Glass Balustrades"] };

export default function EditorialProject({ project, index }) {
  const { lang } = useLang();
  const title = lang === "it" ? project.title_it : project.title_en;
  const desc = lang === "it" ? project.description_it : project.description_en;
  const cat = CAT[project.category]?.[lang === "it" ? 0 : 1] || project.category;
  const variant = index % 3; // 0: image left, 1: image right, 2: full-width
  const num = String(index + 1).padStart(2, "0");

  const Arrow = () => (
    <span className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-[#C8A86B]/50 bg-[#080808]/50 text-[#C8A86B] opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100 group-hover:rotate-45">
      <ArrowUpRight size={18} />
    </span>
  );

  if (variant === 2) {
    return (
      <Link to={`/projects/${project.id}`} data-testid={`editorial-project-${project.slug || project.id}`} className="group block">
        <div className="relative isolate h-[70svh] min-h-[460px] sm:h-[90svh] overflow-hidden border border-[#262420] transition-colors duration-500 group-hover:border-[#C8A86B]/50">
          <ParallaxImage src={project.cover_image} alt={title} className="absolute inset-0 -z-10 transition-[filter] duration-700 group-hover:brightness-110" strength={8} />
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#080808] via-[#080808]/20 to-transparent" />
          <span className="absolute left-5 top-5 font-mono text-xs tracking-[0.3em] text-[#F3EFE6]/70">{num}</span>
          <Arrow />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.25em] text-[#C8A86B]">{cat}</p>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-6xl font-light leading-[1.02] text-[#F3EFE6]">{title}</h3>
            </div>
            <p className="lg:col-span-4 text-xs font-mono uppercase tracking-wider text-[#A39E93] lg:text-right">{project.location} — {project.year}</p>
          </div>
        </div>
      </Link>
    );
  }

  const flip = variant === 1;
  return (
    <Link to={`/projects/${project.id}`} data-testid={`editorial-project-${project.slug || project.id}`} className="group block">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-end">
        <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
          <div className="relative isolate aspect-[4/3] sm:aspect-[16/10] overflow-hidden border border-[#262420] transition-colors duration-500 group-hover:border-[#C8A86B]/50">
            <ParallaxImage src={project.cover_image} alt={title} className="absolute inset-0 -z-10 transition-[filter] duration-700 group-hover:brightness-110" strength={7} sizes="(max-width: 1024px) 100vw, 60vw" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#080808]/50 via-transparent to-transparent" />
            <span className="absolute left-5 top-5 font-mono text-xs tracking-[0.3em] text-[#F3EFE6]/70">{num}</span>
            <Arrow />
          </div>
        </div>
        <div className={`lg:col-span-5 lg:pb-8 ${flip ? "lg:order-1 lg:text-right lg:pr-6" : "lg:pl-6"}`}>
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.25em] text-[#C8A86B]">{cat}</p>
          <h3 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] text-[#F3EFE6] transition-colors duration-300 group-hover:text-[#D8BD85]">{title}</h3>
          <p className="mt-5 text-xs font-mono uppercase tracking-wider text-[#827D72]">{project.location} — {project.year}</p>
          <p className="mt-8 hidden lg:block text-base font-light leading-relaxed text-[#A39E93] line-clamp-3">{desc}</p>
        </div>
      </div>
    </Link>
  );
}
